import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/Button";
import { FormField, Input, Select } from "../../../components/ui/Input";
import api from "../../../services/api";
import { ROLES, extractErrorMessage } from "../utils";

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  username: "",
  password: "",
  role: "VIEWER",
};

export function AddUserModal({ open, onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function reset() {
    setForm(EMPTY_FORM);
    setSaving(false);
    setError(null);
  }

  function handleClose() {
    reset();
    onClose?.();
  }

  function update(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function handleSubmit() {
    setSaving(true);
    setError(null);
    try {
      const { data } = await api.post("/users", form);
      onCreated?.(data);
      handleClose();
    } catch (e) {
      setError(extractErrorMessage(e, t("users.add_user_error_generic")));
    } finally {
      setSaving(false);
    }
  }

  const canSubmit =
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.email.trim() &&
    form.username.trim() &&
    form.password.length >= 8;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("users.add_user_modal_title")}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            {t("common.close")}
          </Button>
          <Button onClick={handleSubmit} loading={saving} disabled={!canSubmit}>
            {t("users.add_user_submit")}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("users.field_first_name")} required>
          <Input value={form.firstName} onChange={update("firstName")} />
        </FormField>
        <FormField label={t("users.field_last_name")} required>
          <Input value={form.lastName} onChange={update("lastName")} />
        </FormField>
        <FormField label={t("users.field_email")} required>
          <Input type="email" value={form.email} onChange={update("email")} />
        </FormField>
        <FormField label={t("users.field_phone")}>
          <Input value={form.phoneNumber} onChange={update("phoneNumber")} />
        </FormField>
        <FormField label={t("users.field_username")} required>
          <Input value={form.username} onChange={update("username")} />
        </FormField>
        <FormField label={t("users.field_role")}>
          <Select value={form.role} onChange={update("role")}>
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {t(`users.role_${role.toLowerCase()}`)}
              </option>
            ))}
          </Select>
        </FormField>
        <div className="col-span-2">
          <FormField
            label={t("users.field_password")}
            required
            helper={t("users.field_password_helper")}
          >
            <Input
              type="text"
              value={form.password}
              onChange={update("password")}
            />
          </FormField>
        </div>
      </div>

      {error && <p className="text-[12px] text-rose-600">{error}</p>}
    </Modal>
  );
}

export default AddUserModal;
